import { create } from 'zustand'
import type { AuthUser } from '@rondela/shared'
import { api, ApiError } from '../../shared/lib/api'

// ===========================================
// Store de autenticación (Zustand)
// ===========================================

interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  error: string | null

  // Acciones
  checkSession: () => Promise<void>
  requestMagicLink: (email: string) => Promise<{ devMagicLink?: string }>
  verifyMagicLink: (token: string) => Promise<void>
  logout: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true, // Comienza cargando para verificar sesión
  error: null,

  checkSession: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ user: AuthUser }>('/api/auth/session')
      set({ user: response.user, isLoading: false })
    } catch {
      // Solo establecer user: null si no hay un usuario ya establecido
      // Esto evita sobrescribir un usuario que fue establecido por verificación
      const currentUser = useAuthStore.getState().user
      if (!currentUser) {
        set({ user: null, isLoading: false })
      } else {
        set({ isLoading: false })
      }
    }
  },

  requestMagicLink: async (email: string) => {
    try {
      set({ error: null })
      const response = await api.post<{ message: string; devMagicLink?: string }>(
        '/api/auth/magic-link',
        { email }
      )
      return { devMagicLink: response.devMagicLink }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al enviar el enlace'
      set({ error: message })
      throw error
    }
  },

  verifyMagicLink: async (token: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.post<{ user: AuthUser }>('/api/auth/verify', { token })
      set({ user: response.user, isLoading: false })
    } catch (error) {
      const message =
        error instanceof ApiError ? error.message : 'Error al verificar el enlace'
      set({ isLoading: false, error: message })
      throw error
    }
  },

  logout: async () => {
    try {
      set({ isLoading: true })
      await api.post('/api/auth/logout')
      set({ user: null, isLoading: false })
    } catch {
      // Limpiar usuario de todas formas
      set({ user: null, isLoading: false })
    }
  },

  clearError: () => set({ error: null }),
}))
